import { useEffect, useState, type RefObject } from "react";

/**
 * The live inner WIDTH of a chart's scroll container, in px.
 *
 * Both Gantt surfaces run their axis to it rather than to their data, so a
 * chart with two rows in it still reads as a chart rather than as a stub
 * floating in white space.
 *
 * Only the width is measured. The height is filled in CSS (`min-h-full` on the
 * content, the body taking `flex-1` of it), which is exact by construction
 * where arithmetic over a header constant was out by its bottom border.
 *
 * `clientWidth`, not `offsetWidth`: it excludes the scrollbars, so the fill
 * targets the box that is actually visible. Together with `daysToFill`
 * rounding DOWN, filling can never widen the content past the box — which is
 * what stops the two scrollbars from summoning each other in a loop.
 */
export function useGanttViewport(ref: RefObject<HTMLElement>): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Same value in means no state change and no re-render: this fires on
    // every resize tick, including the ones our own fill causes.
    const measure = () => setWidth(el.clientWidth);

    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}
